import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  TextField,
  InputAdornment,
  Box,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { ThemePalette } from 'mui.theme';
import useAppTheme from 'hooks/useAppTheme';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface SearchableSelectorProps {
  options: any[];
  selectedValues: any[];
  onSelectionChange: (values: any) => void;
  placeholder?: string;
  label?: string;
  sx?: any;
  multiple?: boolean;
  showSelectAll?: boolean;
  [key: string]: any;
}

const SearchableSelector: React.FC<SearchableSelectorProps> = ({
  options = [],
  selectedValues = [],
  onSelectionChange,
  placeholder = 'Search...',
  label,
  sx = {},
  multiple = true,
  showSelectAll = true,
  ...selectProps
}) => {
  const enableDarkTheme = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    return options.filter((option) =>
      option.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  useEffect(() => {
    if (isDropdownOpen && searchInputRef.current) {
      requestAnimationFrame(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      });
    }
  }, [searchTerm, isDropdownOpen]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    requestAnimationFrame(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    });
  };

  const handleSelectOpen = () => {
    setIsDropdownOpen(true);
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
  };

  const handleSelectClose = () => {
    setIsDropdownOpen(false);
    setSearchTerm('');
  };

  const handleSelectionChange = (e: any) => {
    const selectedValues = e.target.value;

    if (multiple && selectedValues.includes('select-all')) {
      const allSelected = selectedValues.length - 1 === filteredOptions.length;
      if (allSelected) {
        onSelectionChange([]);
      } else {
        onSelectionChange([...filteredOptions]);
      }
    } else {
      onSelectionChange(selectedValues);
    }
  };

  const defaultRenderValue = (selected: any) => {
    if (multiple) {
      return `${selected?.length ?? 0} ${label ?? 'Item'}(s) Selected`;
    }
    return selected ?? '';
  };

  return (
    <Select
      multiple={multiple}
      value={selectedValues}
      renderValue={defaultRenderValue}
      onChange={handleSelectionChange}
      onOpen={handleSelectOpen}
      onClose={handleSelectClose}
      sx={{
        height: '28px',
        fontSize: '14px',
        backgroundColor: enableDarkTheme
          ? ThemePalette?.dark?.cartesianGrid
          : ThemePalette?.light?.menuOptions,
        color: enableDarkTheme
          ? ThemePalette?.typography?.white
          : ThemePalette?.typography?.black,
        '& .MuiSelect-icon': {
          color: enableDarkTheme
            ? ThemePalette?.typography?.lightGrey
            : ThemePalette?.typography?.black,
        },
        '& fieldset': {
          borderColor: enableDarkTheme
            ? ThemePalette?.typography?.white
            : ThemePalette?.typography?.black,
        },
        ...(enableDarkTheme && {
          '&.MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: palette.cardBorderColor,
            },
          },
        }),
        ...sx,
      }}
      MenuProps={{
        PaperProps: {
          style: {
            backgroundColor: enableDarkTheme
              ? ThemePalette?.dark?.menuOptions
              : ThemePalette?.light?.menuOptions,
            color: enableDarkTheme
              ? ThemePalette?.typography?.white
              : ThemePalette?.typography?.black,
            fontSize: '14px',
            maxHeight: '400px',
          },
        },
      }}
      {...selectProps}
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: '1px solid',
          borderColor: enableDarkTheme ? '#2F445D' : '#E0E0E0',
        }}
      >
        <TextField
          placeholder={placeholder}
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearchChange}
          onClick={(e) => {
            e.stopPropagation();
          }}
          onKeyDown={(e) => {
            e.stopPropagation();
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          inputRef={searchInputRef}
          // autoFocus removed since we handle it in onOpen
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  sx={{
                    color: enableDarkTheme
                      ? ThemePalette?.typography?.lightGrey
                      : ThemePalette?.typography?.black,
                  }}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            width: '100%',
            '& .MuiOutlinedInput-root': {
              backgroundColor: enableDarkTheme
                ? ThemePalette?.dark?.cartesianGrid
                : ThemePalette?.light?.menuOptions,
              color: enableDarkTheme
                ? ThemePalette?.typography?.white
                : ThemePalette?.typography?.black,
              '& fieldset': {
                borderColor: enableDarkTheme ? '#2F445D' : '#E0E0E0',
              },
              '&:hover fieldset': {
                borderColor: enableDarkTheme ? '#4A5F7A' : '#B0B0B0',
              },
              '&.Mui-focused fieldset': {
                borderColor: enableDarkTheme
                  ? ThemePalette?.typography?.cornflowerBlue
                  : ThemePalette?.typography?.primary,
              },
            },
            '& .MuiInputBase-input': {
              color: enableDarkTheme
                ? ThemePalette?.typography?.white
                : ThemePalette?.typography?.black,
            },
            '& .MuiInputBase-input::placeholder': {
              color: enableDarkTheme
                ? ThemePalette?.typography?.lightGrey
                : ThemePalette?.typography?.secondary,
              opacity: 1,
            },
          }}
        />
      </Box>

      {multiple && showSelectAll && (
        <MenuItem
          key="select-all-option"
          value="select-all"
          sx={{
            fontSize: '14px',
            borderBottom: '1px solid',
            borderColor: enableDarkTheme ? '#2F445D' : '#E0E0E0',
          }}
        >
          <Checkbox
            sx={{
              color: enableDarkTheme
                ? ThemePalette?.typography?.cornflowerBlue
                : ThemePalette?.typography?.primary,
              '&.Mui-checked': {
                color: enableDarkTheme
                  ? ThemePalette?.typography?.cornflowerBlue
                  : ThemePalette?.typography?.primary,
              },
              '&.Mui-disabled': {
                color: ThemePalette?.typography?.secondary,
              },
            }}
            checked={
              selectedValues.length === filteredOptions.length &&
              filteredOptions.length > 0
            }
            indeterminate={
              selectedValues.length > 0 &&
              selectedValues.length < filteredOptions.length
            }
          />
          <ListItemText primary="Select All" />
        </MenuItem>
      )}

      {filteredOptions.map((option, index) => (
        <MenuItem
          key={index}
          value={option}
          sx={{
            fontSize: '14px',
          }}
        >
          {multiple && (
            <Checkbox
              sx={{
                color: enableDarkTheme
                  ? ThemePalette?.typography?.cornflowerBlue
                  : ThemePalette?.typography?.primary,
                '&.Mui-checked': {
                  color: enableDarkTheme
                    ? ThemePalette?.typography?.cornflowerBlue
                    : ThemePalette?.typography?.primary,
                },
                '&.Mui-disabled': {
                  color: ThemePalette?.typography?.secondary,
                },
              }}
              checked={selectedValues.includes(option)}
            />
          )}
          <ListItemText primary={option} />
        </MenuItem>
      ))}
    </Select>
  );
};

export default SearchableSelector;
